/**
 * Instalação do app (PWA). O Chrome/Edge disparam `beforeinstallprompt` cedo, antes do React montar; o evento fica
 * guardado aqui (importar este módulo no boot já basta) e `useInstalar` o expõe às telas. No iPhone não há evento:
 * a tela mostra a instrução "Adicionar à Tela de Início". Instalado (display-mode: standalone) esconde tudo.
 */
import { useSyncExternalStore } from 'react';

/** Evento do Chrome/Edge (não está no lib.dom). */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface Instalacao {
  /** Há convite nativo pendente (Chrome/Edge, computador ou Android). */
  convite: boolean;
  /** Já roda instalado (standalone). */
  instalado: boolean;
  /** iPhone/iPad: sem convite nativo; a instrução manual vale. */
  ios: boolean;
}

let evento: BeforeInstallPromptEvent | null = null;
let snapshot: Instalacao = { convite: false, instalado: false, ios: false };
const ouvintes = new Set<() => void>();
const avisar = () => ouvintes.forEach((f) => f());

/** Rodando como app instalado (ícone próprio), não numa aba. */
export const instalado = (): boolean =>
  typeof window !== 'undefined' &&
  (window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: window-controls-overlay)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true);

/** iPhone ou iPad (o iPad se apresenta como Mac, mas com toque). */
export const ehIOS = (): boolean =>
  typeof navigator !== 'undefined' &&
  (/iPhone|iPad|iPod/.test(navigator.userAgent) || (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1));

const recalcular = () => {
  snapshot = { convite: evento !== null && !instalado(), instalado: instalado(), ios: ehIOS() };
  avisar();
};

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    evento = e as BeforeInstallPromptEvent;
    recalcular();
  });
  window.addEventListener('appinstalled', () => {
    evento = null;
    recalcular();
  });
  window.matchMedia('(display-mode: standalone)').addEventListener('change', recalcular);
  recalcular();
}

const assinar = (f: () => void) => {
  ouvintes.add(f);
  return () => {
    ouvintes.delete(f);
  };
};
const ler = () => snapshot;
const lerServidor = (): Instalacao => ({ convite: false, instalado: false, ios: false });

/** Estado da instalação, vivo (muda quando o navegador oferece o convite ou quando o app é instalado). */
export function useInstalar(): Instalacao {
  return useSyncExternalStore(assinar, ler, lerServidor);
}

/** Abre o convite nativo de instalação; devolve se a pessoa aceitou. Sem convite, não faz nada. */
export async function instalar(): Promise<boolean> {
  const e = evento;
  if (!e) return false;
  evento = null;
  recalcular();
  try {
    await e.prompt();
    const { outcome } = await e.userChoice;
    return outcome === 'accepted';
  } catch {
    return false;
  }
}
